import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import {
    APP_INITIALIZER,
    ApplicationConfig,
    ErrorHandler,
    LOCALE_ID,
    Provider,
    importProvidersFrom,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideRouter, withViewTransitions } from '@angular/router';

import { ConfigurationService as CheckoutConfigurationService, CheckoutModule } from '@bytel/checkout';
import { AngularPrismeLogger } from '@bytel/prisme-logger/lib/angular';

import { ProductHelper } from '@base/catalog/product-helper';
import { PhonePipe } from '@base/common/pipes/phone.pipe';
import { CacheHttpInterceptor } from '@base/common/services/cache-http.interceptor';
import { MocksHandlerModule } from '@base/mocks-handler/mocks-handler.module';

import { ConfigurationService } from '@services/configuration.service';
import { initializeProvider } from '@services/initialize.provider';
import { PrismeErrorService } from '@services/prisme-error.service';

import { PaymentResolver } from '@tunnel/payment/payment.resolver';

import { IInitialConfig } from '@interfaces/config.interface';

import { PriceInitialPipe } from '@shared/pipes/price-initial.pipe';

import { routes } from './app.routes';

const CONFIG: () => IInitialConfig = () => window.ConfigInitial;

export const getPrismeLoggerInterceptorProvider = (): Provider => ({
    provide: HTTP_INTERCEPTORS,
    useFactory: (prismeLogger: AngularPrismeLogger) => prismeLogger.getInterceptor(),
    multi: true,
    deps: [AngularPrismeLogger],
});

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(routes, withViewTransitions()),
        importProvidersFrom(BrowserModule, MocksHandlerModule, CommonModule, CheckoutModule.forRoot(CONFIG)),
        PriceInitialPipe,
        ProductHelper,
        PaymentResolver,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CacheHttpInterceptor,
            multi: true,
        },
        {
            provide: LOCALE_ID,
            useValue: 'fr-FR',
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeProvider,
            deps: [ConfigurationService, CheckoutConfigurationService],
            multi: true,
        },
        PhonePipe,
        {
            provide: APP_BASE_HREF,
            useValue: '/',
        },
        {
            provide: AngularPrismeLogger,
            useFactory: (): AngularPrismeLogger =>
                new AngularPrismeLogger({
                    st: window.ConfigInitial.oauth.xSource,
                    environnement: window.ConfigInitial.environment,
                    urlEndpoint: window.ConfigInitial.oauth.resourceUrl + '/traces-techniques',
                    tailleMaxEnvoiQueueMessages: 5,
                }),
        },
        getPrismeLoggerInterceptorProvider(),
        {
            provide: ErrorHandler,
            useClass: PrismeErrorService,
        },
        provideHttpClient(withJsonpSupport(), withInterceptorsFromDi()),
    ],
};
